/**
 * VideoPose3D 2D→3D pose lifter (Mobile tier).
 *
 * Window: 81 frames. Input shape: [1, 81, 17, 2]. Output: [1, 17, 3]
 * for the *centre frame only* (the original VideoPose3D causal head).
 * The PoseLifter wrapper expects per-frame output, so we tile the
 * centre-frame result across the window's central span and let the
 * sliding-window blender average overlapping coverage.
 *
 * docs/migration-plan.md Phase 12.
 */

import * as ort from 'onnxruntime-web';

import { ensureOrtWasmEnv, fetchModelCached } from '../onnx-runtime';
import type { PoseLifterAdapter } from '../../core/PoseLifter';

export interface VideoPose3DOptions {
  /** Override URL for self-hosting. Default: /models/videopose3d-81-17.onnx. */
  modelUrl?: string;
}

const DEFAULT_MODEL_URL = '/models/videopose3d-81-17.onnx';
const WINDOW_FRAMES = 81;
const NUM_JOINTS = 17;

export class VideoPose3DLifter implements PoseLifterAdapter {
  readonly id = 'videopose3d@1.1';
  readonly windowFrames = WINDOW_FRAMES;
  readonly tier = 'mobile' as const;

  private session: ort.InferenceSession | null = null;
  private inputName: string | null = null;
  private outputName: string | null = null;

  constructor(private readonly opts: VideoPose3DOptions = {}) {}

  async initialize(): Promise<void> {
    if (this.session) return;
    ensureOrtWasmEnv();
    const url = this.opts.modelUrl ?? DEFAULT_MODEL_URL;
    const { bytes, diag } = await fetchModelCached(url);
    console.info('[videopose3d]', diag.source, `${(diag.byteLength / 1e6).toFixed(1)}MB`, `${diag.ms.toFixed(0)}ms`);
    this.session = await ort.InferenceSession.create(bytes, {
      // Mobile path stays on wasm-simd for predictable thermal
      // behaviour. WebGPU on mobile Safari still has rough edges.
      executionProviders: ['wasm'],
      graphOptimizationLevel: 'all',
    });
    this.inputName = this.session.inputNames[0];
    this.outputName = this.session.outputNames[0];
  }

  async liftWindow(
    keypoints2D: Float32Array,
    numFrames: number,
    numJoints: number,
  ): Promise<Float32Array | null> {
    if (!this.session || !this.inputName || !this.outputName) return null;
    if (numFrames !== WINDOW_FRAMES || numJoints !== NUM_JOINTS) {
      throw new Error(
        `VideoPose3DLifter expects [${WINDOW_FRAMES}, ${NUM_JOINTS}, 2]; got [${numFrames}, ${numJoints}, 2]`,
      );
    }
    const tensor = new ort.Tensor('float32', keypoints2D, [
      1,
      WINDOW_FRAMES,
      NUM_JOINTS,
      2,
    ]);
    const result = await this.session.run({ [this.inputName]: tensor });
    const centre = result[this.outputName].data as Float32Array;
    if (!centre || centre.length !== NUM_JOINTS * 3) return null;
    // Tile the centre-frame prediction across the window so the
    // sliding-window blender in PoseLifter receives a per-frame
    // signal it can average.
    const out = new Float32Array(WINDOW_FRAMES * NUM_JOINTS * 3);
    for (let f = 0; f < WINDOW_FRAMES; f += 1) {
      out.set(centre, f * NUM_JOINTS * 3);
    }
    return out;
  }

  async dispose(): Promise<void> {
    try {
      await this.session?.release?.();
    } catch {
      /* ignore */
    }
    this.session = null;
    this.inputName = null;
    this.outputName = null;
  }
}
